import type { ArticleDetails, AuthorDto } from '../../../api/responses'
import type { SeoCitation } from '../../../shared/seo/seo.types'
import { firstText } from '../../../shared/seo/seo.util'
import { ArticleType } from '../../../types/types'

/** The publisher's own scholarly pieces; Scholar excludes news and editorials. */
const SCHOLARLY_ARTICLE_TYPES: string[] = [
  ArticleType.ARTICLE,
  ArticleType.CASE_LAW_DISCUSSION,
  ArticleType.ANNOTATION,
]

const PUBLICATION_DATE_FORMAT = new Intl.DateTimeFormat('en-CA', {
  timeZone: 'Europe/Amsterdam',
  year: 'numeric',
  month: '2-digit',
  day: '2-digit',
})

/** The API returns no edition for some articles, despite `ArticleDetails`. */
type CitedArticle = Omit<ArticleDetails, 'edition'> & {
  edition: ArticleDetails['edition'] | null
}

/** Google Scholar citation data for an article, or null for other types. */
export function articleCitation(article: CitedArticle): SeoCitation | null {
  if (!SCHOLARLY_ARTICLE_TYPES.includes(article.article_type?.name ?? '')) {
    return null
  }

  // The same people the page lists under Auteur(s), see ArticleMetaData.
  const people = article.users?.authors?.length
    ? article.users.authors
    : (article.users?.creators ?? [])

  return {
    title: article.title,
    authors: people.map(citationName).filter((name): name is string => !!name),
    publicationDate: publicationDate(article.published_at),
    journalTitle: article.magazine?.title,
    issns: [article.magazine?.issn_digital, article.magazine?.issn_folio]
      .map(issn => firstText(issn))
      .filter((issn): issn is string => !!issn),
    volume: article.edition?.year?.toString(),
    issue: article.edition?.edition_version,
    firstPage: article.page_number_start?.toString(),
    lastPage: article.page_number_end?.toString(),
  }
}

/** The name as it is cited ("S. van Schaik"), without titles. */
function citationName(author: AuthorDto): string | undefined {
  const lastName = firstText(author.last_name)
  if (!lastName) return firstText(author.name)

  return [
    firstText(author.initials, author.first_name),
    firstText(author.middle_name),
    lastName,
  ]
    .filter(Boolean)
    .join(' ')
}

/** `yyyy/MM/dd` in Dutch time, the date the page shows. */
function publicationDate(
  publishedAt: string | null | undefined
): string | null {
  const date = new Date(publishedAt ?? '')
  if (isNaN(date.getTime())) return null

  const parts = Object.fromEntries(
    PUBLICATION_DATE_FORMAT.formatToParts(date).map(({ type, value }) => [
      type,
      value,
    ])
  )

  return `${parts['year']}/${parts['month']}/${parts['day']}`
}
